/* eslint-disable i18next/no-literal-string */

/**
 * Frontend span collector for the E2E request observability feature.
 *
 * Registers a PerformanceObserver for `navigation`, `paint`, and `longtask` entry types on
 * module init. Accumulates performance entries in a per-action buffer. When an action ends
 * (via the flushAction callback wired into startAction), posts one batched request:
 *   - One FRONTEND_ACTION span for the action envelope
 *   - An optional FRONTEND_RENDER span if React render timing was collected
 *
 * Fire-and-forget semantics: non-2xx and network errors are silently logged via console.warn
 * and never surface as UI errors or unhandled promise rejections.
 *
 * v1 tab-close behavior: in-flight spans for an unfinished action are dropped when the page
 * unloads. No sendBeacon is used in v1.
 * @see https://github.com/your-org/iam2/blob/main/docs/specs/e2e-request-observability/architecture.md#126-open-questions
 * Spec §12.6 Open Question 6 — sendBeacon deferred to v1+.
 */

import {endpoints} from '@/api/endpoints'

// ============================================================================
// Types
// ============================================================================

/** Allowed span kind values, mirroring the Java {@code FrontendSpanRequest.Span.kind} discriminator. */
export const SpanKind = {
  FRONTEND_ACTION: 'FRONTEND_ACTION',
  FRONTEND_RENDER: 'FRONTEND_RENDER',
} as const
export type SpanKind = (typeof SpanKind)[keyof typeof SpanKind]

/**
 * Shape of a single span in the POST body.
 * Mirrors the Java FrontendSpanRequest.Span record.
 */
export interface FrontendSpanDto {
  kind: SpanKind
  operationName: string
  actionId: string
  startedAt: string
  durationMs: number
  attributes?: Record<string, unknown>
}

/**
 * Shape of the POST /api/i/observability/frontend-spans request body.
 * Mirrors the Java FrontendSpanRequest record.
 */
export interface FrontendSpanRequest {
  sessionId: string
  spans: FrontendSpanDto[]
}

// ============================================================================
// Internal state
// ============================================================================

/** Performance entries accumulated during the current action window. */
let currentActionEntries: PerformanceEntry[] = []

/** Whether the PerformanceObserver has been registered. */
let observerRegistered = false

// ============================================================================
// PerformanceObserver — registered once on module init
// ============================================================================

function initPerformanceObserver(): void {
  if (observerRegistered) {
    return
  }
  observerRegistered = true

  if (typeof PerformanceObserver === 'undefined') {
    return
  }

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      currentActionEntries.push(entry)
    }
  })

  const supportedTypes: string[] = []
  for (const type of ['navigation', 'paint', 'longtask']) {
    try {
      if (PerformanceObserver.supportedEntryTypes?.includes(type)) {
        supportedTypes.push(type)
      }
    } catch (e) {
      console.debug('[spanCollector] PerformanceObserver.supportedEntryTypes check failed for type', type, e)
    }
  }

  if (supportedTypes.length > 0) {
    try {
      observer.observe({entryTypes: supportedTypes})
    } catch (e) {
      console.debug('[spanCollector] PerformanceObserver.observe failed', e)
    }
  }
}

// Initialize on module load
initPerformanceObserver()

// ============================================================================
// flushAction — builds and posts the span batch
// ============================================================================

/**
 * Builds a FRONTEND_ACTION span (and optionally FRONTEND_RENDER) for the completed action
 * and posts the batch to the backend. Fire-and-forget — never throws.
 *
 * @param actionId   the UUID of the action that just ended
 * @param label      the action label (used as operationName)
 * @param startedAt  when the action started
 * @param durationMs how long the action took in milliseconds
 * @param sessionId  the current browser session id
 */
export async function flushAction(
  actionId: string,
  label: string,
  startedAt: Date,
  durationMs: number,
  sessionId: string
): Promise<void> {
  const entriesSnapshot = currentActionEntries
  currentActionEntries = []

  try {
    const spans: FrontendSpanDto[] = []

    const navigationTiming = extractNavigationTiming(entriesSnapshot)
    const paintTiming = extractPaintTiming(entriesSnapshot)
    const longTasks = extractLongTasks(entriesSnapshot)

    const actionAttributes: Record<string, unknown> = {}
    if (navigationTiming !== null) {
      actionAttributes.navigationTiming = navigationTiming
    }
    if (paintTiming !== null) {
      actionAttributes.paintTiming = paintTiming
    }
    if (longTasks !== null) {
      actionAttributes.longTasks = longTasks
    }

    spans.push({
      kind: SpanKind.FRONTEND_ACTION,
      operationName: label,
      actionId,
      startedAt: startedAt.toISOString(),
      durationMs,
      attributes: actionAttributes,
    })

    const renderMs = extractRenderMs(entriesSnapshot)
    if (renderMs !== null) {
      spans.push({
        kind: SpanKind.FRONTEND_RENDER,
        operationName: `render:${label}`,
        actionId,
        startedAt: startedAt.toISOString(),
        durationMs: renderMs,
        attributes: {renderMs},
      })
    }

    const request: FrontendSpanRequest = {sessionId, spans}

    const response = await fetch(endpoints.observability.frontendSpans(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionId,
      },
      credentials: 'include',
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      console.warn('[observability] frontend span POST failed:', response.status, response.statusText)
    }
  } catch (err) {
    console.warn('[observability] frontend span POST error:', err)
  }
}

// ============================================================================
// Performance entry helpers
// ============================================================================

function extractNavigationTiming(entries: PerformanceEntry[]): unknown | null {
  const nav = entries.find((e) => e.entryType === 'navigation')
  if (!nav) {
    return null
  }
  const navEntry = nav as PerformanceNavigationTiming
  return {
    domContentLoadedMs: Math.round(navEntry.domContentLoadedEventEnd),
    loadEventMs: Math.round(navEntry.loadEventEnd),
    domInteractiveMs: Math.round(navEntry.domInteractive),
  }
}

function extractPaintTiming(entries: PerformanceEntry[]): unknown | null {
  const paints = entries.filter((e) => e.entryType === 'paint')
  if (paints.length === 0) {
    return null
  }
  const result: Record<string, number> = {}
  for (const p of paints) {
    result[p.name.replace(/-/g, '_')] = Math.round(p.startTime)
  }
  return result
}

function extractLongTasks(entries: PerformanceEntry[]): unknown | null {
  const tasks = entries.filter((e) => e.entryType === 'longtask')
  if (tasks.length === 0) {
    return null
  }
  return tasks.map((t) => ({durationMs: Math.round(t.duration), startMs: Math.round(t.startTime)}))
}

function extractRenderMs(entries: PerformanceEntry[]): number | null {
  const measure = entries.find(
    (e) => e.entryType === 'measure' && e.name.startsWith('react-render:')
  )
  if (!measure) {
    return null
  }
  return Math.round(measure.duration)
}

// ============================================================================
// Test-only reset
// ============================================================================

/** Test-only: reset module state between Vitest runs. */
export function _resetCollectorForTest(): void {
  currentActionEntries = []
  observerRegistered = false
}

/** Test-only: inject a performance entry directly into the current action buffer. */
export function _injectEntryForTest(entry: PerformanceEntry): void {
  currentActionEntries.push(entry)
}
